// Slice 3 — Timesheet preview.
// Pick a pay-period date range, see per-barber per-day hours derived from
// barber_shift minus barber_break, in Brisbane local time. Read-only — Slice
// 4 will add the "Push to Xero (DRAFT)" button.

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api';

const BRISBANE_TZ = 'Australia/Brisbane';

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--spacing-4)',
  boxShadow: 'var(--shadow-sm)',
};

const inputStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  backgroundColor: 'var(--color-background)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text)',
};

const presetButtonStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  borderRadius: '999px',
  border: '1px solid var(--color-border)',
  backgroundColor: 'var(--color-surface)',
  color: 'var(--color-text)',
  fontWeight: 600,
  fontSize: 'var(--font-size-sm)',
  cursor: 'pointer',
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: 'var(--spacing-2) var(--spacing-3)',
  borderBottom: '2px solid var(--color-border)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--color-text-muted)',
};

const tdStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  borderBottom: '1px solid var(--color-border)',
  fontSize: 'var(--font-size-sm)',
};

function brisbaneDate(d: Date = new Date()): string {
  return d.toLocaleDateString('en-CA', { timeZone: BRISBANE_TZ });
}

function addDays(dateString: string, days: number) {
  const d = new Date(`${dateString}T12:00:00`);
  d.setDate(d.getDate() + days);
  return brisbaneDate(d);
}

function startOfWeek(dateString: string) {
  const d = new Date(`${dateString}T12:00:00`);
  const dow = d.getDay();
  const diff = dow === 0 ? -6 : 1 - dow;
  d.setDate(d.getDate() + diff);
  return brisbaneDate(d);
}

type Preset = 'THIS_WEEK' | 'LAST_WEEK' | 'THIS_FORTNIGHT' | 'LAST_FORTNIGHT' | 'CUSTOM';

const PRESET_LABELS: Record<Preset, string> = {
  THIS_WEEK: 'This Week',
  LAST_WEEK: 'Last Week',
  THIS_FORTNIGHT: 'This Fortnight',
  LAST_FORTNIGHT: 'Last Fortnight',
  CUSTOM: 'Custom',
};

const PRESETS: Preset[] = ['THIS_WEEK', 'LAST_WEEK', 'THIS_FORTNIGHT', 'LAST_FORTNIGHT', 'CUSTOM'];

function rangeFromPreset(preset: Preset): { startDate: string; endDate: string } {
  const today = brisbaneDate();
  const ws = startOfWeek(today);
  switch (preset) {
    case 'THIS_WEEK':
      return { startDate: ws, endDate: addDays(ws, 6) };
    case 'LAST_WEEK': {
      const lws = addDays(ws, -7);
      return { startDate: lws, endDate: addDays(lws, 6) };
    }
    case 'THIS_FORTNIGHT': {
      const fws = addDays(ws, -7);
      return { startDate: fws, endDate: addDays(fws, 13) };
    }
    case 'LAST_FORTNIGHT': {
      const fws = addDays(ws, -21);
      return { startDate: fws, endDate: addDays(fws, 13) };
    }
    case 'CUSTOM':
    default:
      return { startDate: ws, endDate: addDays(ws, 6) };
  }
}

function formatDate(d: string) {
  const dt = new Date(`${d}T12:00:00`);
  return dt.toLocaleDateString('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: BRISBANE_TZ,
  });
}

export function PayrollTimesheets() {
  const queryClient = useQueryClient();
  const [preset, setPreset] = useState<Preset>('LAST_WEEK');
  const [customRange, setCustomRange] = useState(() => rangeFromPreset('LAST_WEEK'));
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [pushingBarberId, setPushingBarberId] = useState<number | null>(null);
  const [pushError, setPushError] = useState<string | null>(null);

  const { data: status, isLoading: statusLoading } = useQuery({
    queryKey: ['xero-status'],
    queryFn: () => api.xeroStatus(),
  });

  const tenants = useMemo(() => status?.tenants ?? [], [status]);

  // Default tenant when first loaded.
  useEffect(() => {
    if (!selectedTenantId && tenants.length > 0) {
      setSelectedTenantId(tenants[0].tenantId);
    }
  }, [tenants, selectedTenantId]);

  const range = preset === 'CUSTOM' ? customRange : rangeFromPreset(preset);

  const { data, isLoading, error } = useQuery({
    queryKey: ['timesheet-preview', selectedTenantId, range.startDate, range.endDate],
    queryFn: () => api.payrollTimesheetPreview(selectedTenantId, range.startDate, range.endDate),
    enabled: Boolean(selectedTenantId) && Boolean(range.startDate) && Boolean(range.endDate),
  });

  const { data: pushedData } = useQuery({
    queryKey: ['timesheet-pushed', selectedTenantId, range.startDate, range.endDate],
    queryFn: () => api.payrollTimesheetsPushed(selectedTenantId, range.startDate, range.endDate),
    enabled: Boolean(selectedTenantId) && Boolean(range.startDate) && Boolean(range.endDate),
  });

  const pushedByBarber = useMemo(() => {
    const m = new Map<
      number,
      {
        xeroTimesheetId: string;
        status: string;
        updatedAt: string;
        startDate: string;
        endDate: string;
      }
    >();
    for (const p of pushedData?.pushed ?? []) m.set(p.barberId, p);
    return m;
  }, [pushedData]);

  const rows = data?.rows ?? [];

  const handlePreset = (p: Preset) => {
    setPreset(p);
    if (p !== 'CUSTOM') setCustomRange(rangeFromPreset(p));
  };

  async function handlePush(barberId: number) {
    if (!selectedTenantId) return;
    setPushingBarberId(barberId);
    setPushError(null);
    try {
      await api.payrollTimesheetPush({
        tenantId: selectedTenantId,
        barberId,
        startDate: range.startDate,
        endDate: range.endDate,
      });
      queryClient.invalidateQueries({
        queryKey: ['timesheet-pushed', selectedTenantId, range.startDate, range.endDate],
      });
    } catch (err) {
      setPushError(err instanceof Error ? err.message : 'Push failed');
    } finally {
      setPushingBarberId(null);
    }
  }

  return (
    <div
      style={{
        padding: 'var(--spacing-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-4)',
        maxWidth: 1200,
      }}
    >
      <div>
        <h1 style={{ margin: 0, marginBottom: 4 }}>Payroll · Timesheets</h1>
        <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
          Hours are calculated automatically from each barber's roster (shifts minus breaks). You
          don't enter timesheet hours by hand — you just send them to Xero.
        </div>
        <ol
          style={{
            color: 'var(--color-text)',
            fontSize: 'var(--font-size-sm)',
            margin: '8px 0 0',
            paddingLeft: 20,
          }}
        >
          <li>
            <strong>Pick the pay period</strong> below (e.g. Last Week or Last Fortnight).
          </li>
          <li>
            <strong>Check each barber's hours</strong> in the table — only mapped barbers appear.{' '}
            <Link to="/payroll/settings" style={{ color: 'var(--color-accent)' }}>
              Map a barber →
            </Link>
          </li>
          <li>
            <strong>Click "Send to Xero"</strong> on each row. We auto-align the dates to the
            barber's Xero pay calendar, so a "Last Week" preview will push a fortnight if the
            barber's Xero calendar is fortnightly.
          </li>
          <li>
            <strong>Approve in Xero</strong> (Payroll → Timesheets) before the pay run is posted.
          </li>
        </ol>
      </div>

      <div style={cardStyle}>
        <div
          style={{
            display: 'flex',
            gap: 'var(--spacing-3)',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
            marginBottom: 'var(--spacing-3)',
          }}
        >
          {tenants.length > 1 && (
            <div>
              <div
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-muted)',
                  marginBottom: 4,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Xero organisation
              </div>
              <select
                style={inputStyle}
                value={selectedTenantId}
                onChange={(e) => {
                  const value = e.currentTarget.value;
                  setSelectedTenantId(value);
                }}
              >
                {tenants.map((t) => (
                  <option key={t.tenantId} value={t.tenantId}>
                    {t.tenantName}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-2)' }}>
            {PRESETS.map((p) => (
              <button
                key={p}
                onClick={() => handlePreset(p)}
                style={{
                  ...presetButtonStyle,
                  backgroundColor: preset === p ? 'var(--color-accent)' : 'var(--color-surface)',
                  color: preset === p ? '#1e1e1e' : 'var(--color-text)',
                  borderColor: preset === p ? 'var(--color-accent)' : 'var(--color-border)',
                }}
              >
                {PRESET_LABELS[p]}
              </button>
            ))}
          </div>

          {preset === 'CUSTOM' && (
            <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
              <input
                type="date"
                value={customRange.startDate}
                onChange={(e) => {
                  const value = e.currentTarget.value;
                  setCustomRange((r) => ({ ...r, startDate: value }));
                }}
                style={inputStyle}
              />
              <input
                type="date"
                value={customRange.endDate}
                onChange={(e) => {
                  const value = e.currentTarget.value;
                  setCustomRange((r) => ({ ...r, endDate: value }));
                }}
                style={inputStyle}
              />
            </div>
          )}
        </div>

        <div
          style={{
            color: 'var(--color-text-muted)',
            fontSize: 'var(--font-size-xs)',
            marginBottom: 'var(--spacing-2)',
          }}
        >
          {formatDate(range.startDate)} → {formatDate(range.endDate)}
        </div>

        {statusLoading ? (
          <div>Loading…</div>
        ) : tenants.length === 0 ? (
          <div
            style={{
              padding: 'var(--spacing-4)',
              border: '1px dashed var(--color-border)',
              borderRadius: 'var(--radius-default)',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-muted)',
                marginBottom: 'var(--spacing-2)',
              }}
            >
              No Xero organisation connected. Connect Xero in{' '}
              <Link to="/payroll/settings" style={{ color: 'var(--color-accent)' }}>
                Settings
              </Link>{' '}
              first.
            </div>
          </div>
        ) : isLoading ? (
          <div>Loading timesheet…</div>
        ) : error ? (
          <div style={{ color: 'var(--color-danger, #e53e3e)' }}>
            Could not load timesheet: {error instanceof Error ? error.message : 'unknown error'}
          </div>
        ) : rows.length === 0 ? (
          <div
            style={{
              padding: 'var(--spacing-4)',
              border: '1px dashed var(--color-border)',
              borderRadius: 'var(--radius-default)',
              textAlign: 'center',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-muted)',
            }}
          >
            No mapped barbers, or no shifts in this period. Map barbers to Xero employees in{' '}
            <Link to="/payroll/settings" style={{ color: 'var(--color-accent)' }}>
              Settings
            </Link>
            .
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Barber</th>
                <th style={thStyle}>Xero employee</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Ordinary</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Saturday</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Sunday</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Total</th>
                <th style={{ ...thStyle, textAlign: 'right' }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const pushed = pushedByBarber.get(r.barberId);
                const isPushing = pushingBarberId === r.barberId;
                return (
                  <tr key={r.barberId}>
                    <td style={tdStyle}>
                      <strong>{r.barberName}</strong>
                      {r.days.length > 0 && (
                        <div
                          style={{
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-text-muted)',
                            marginTop: 2,
                          }}
                        >
                          {r.days
                            .map(
                              (d) => `${formatDate(d.date).split(' ')[0]} ${d.hours.toFixed(2)}h`,
                            )
                            .join(' · ')}
                        </div>
                      )}
                    </td>
                    <td style={tdStyle}>{r.xeroEmployeeName}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      {r.ordinaryHours.toFixed(2)}h
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      {r.saturdayHours > 0 ? (
                        <span style={{ color: '#d6932a', fontWeight: 600 }}>
                          {r.saturdayHours.toFixed(2)}h
                        </span>
                      ) : (
                        <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                      )}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      {r.sundayHours > 0 ? (
                        <span style={{ color: '#ae4132', fontWeight: 600 }}>
                          {r.sundayHours.toFixed(2)}h
                        </span>
                      ) : (
                        <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                      )}
                    </td>
                    <td
                      style={{
                        ...tdStyle,
                        textAlign: 'right',
                        fontWeight: 600,
                      }}
                    >
                      {r.totalHours.toFixed(2)}h
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {pushed ? (
                        <div
                          style={{
                            display: 'inline-flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                            fontSize: 'var(--font-size-xs)',
                          }}
                        >
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              borderRadius: 999,
                              backgroundColor: 'rgba(72, 187, 120, 0.15)',
                              color: '#2f855a',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                            }}
                            title={`Pushed pay period: ${pushed.startDate} → ${pushed.endDate}`}
                          >
                            {pushed.status}
                          </span>
                          <span
                            style={{
                              color: 'var(--color-text-muted)',
                              marginTop: 2,
                            }}
                          >
                            {pushed.startDate} → {pushed.endDate}
                          </span>
                          <button
                            onClick={() => handlePush(r.barberId)}
                            disabled={isPushing || r.totalHours === 0}
                            style={{
                              marginTop: 4,
                              padding: '2px 8px',
                              borderRadius: 'var(--radius-default)',
                              border: '1px solid var(--color-border)',
                              backgroundColor: 'transparent',
                              color: 'var(--color-text-muted)',
                              fontSize: 'var(--font-size-xs)',
                              cursor: 'pointer',
                            }}
                          >
                            {isPushing ? 'Sending…' : 'Re-send'}
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handlePush(r.barberId)}
                          disabled={isPushing || r.totalHours === 0}
                          style={{
                            padding: 'var(--spacing-1) var(--spacing-3)',
                            borderRadius: 'var(--radius-default)',
                            border: 'none',
                            backgroundColor: 'var(--color-accent)',
                            color: '#1e1e1e',
                            fontWeight: 600,
                            fontSize: 'var(--font-size-xs)',
                            cursor: r.totalHours === 0 ? 'not-allowed' : 'pointer',
                            opacity: r.totalHours === 0 ? 0.5 : 1,
                          }}
                        >
                          {isPushing ? 'Sending…' : 'Send to Xero'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {pushError && (
          <div
            style={{
              marginTop: 'var(--spacing-3)',
              padding: 'var(--spacing-2) var(--spacing-3)',
              backgroundColor: 'rgba(229, 62, 62, 0.1)',
              border: '1px solid var(--color-danger, #e53e3e)',
              borderRadius: 'var(--radius-default)',
              color: 'var(--color-danger, #e53e3e)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            {pushError}
          </div>
        )}
      </div>
    </div>
  );
}
